import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Tabs,
    TabsHeader,
    TabsBody,
    Tab,
    TabPanel,
} from "@material-tailwind/react";
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

// Helper component for Modals
const Modal = ({ isOpen, onClose, title, children }) => {
    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <motion.div
                        className="bg-zinc-850 rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[85vh] flex flex-col"
                        initial={{ y: 30, opacity: 0, scale: 0.95 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 30, opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: "circOut" }}
                    >
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold text-sky-300">{title}</h3>
                            <button
                                onClick={onClose}
                                className="text-zinc-400 hover:text-white transition-colors"
                            >
                                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                        <div className="overflow-y-auto flex-grow custom-scrollbar pr-2">
                            {children}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const PathCompetitiveAnalysis = ({ competitiveAnalysisResult }) => {
    const [activeTab, setActiveTab] = useState('Talking Points');
    const [expandedReasoning, setExpandedReasoning] = useState({});

    // console.log('*** --------------------------------')
    // console.log('competitiveAnalysisResult', competitiveAnalysisResult);
    // console.log('*** --------------------------------')

    const battleCard = competitiveAnalysisResult?.battleCard;

    if (!battleCard) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-zinc-400 p-8 text-center min-h-[calc(100vh-10rem)] bg-zinc-950">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-16 h-16 mb-4 text-zinc-500">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
                <h3 className="text-xl font-semibold text-zinc-200 mb-2">No Competitive Analysis Available</h3>
                <p className="text-zinc-400">There are currently no competitive analysis details to display.</p>
            </div>
        );
    }

    const { headline, whyRelevant, comparisonTable, actionableTalkingPoints, sources } = battleCard;

    const competitorNames = comparisonTable && comparisonTable.length > 0 
        ? comparisonTable[0].competitors.map(c => c.competitorName) 
        : [];

    const getAdvantageClass = (advantage) => {
        switch (advantage) {
            case 'advantage': return 'text-green-400 bg-green-800/10 text-sm';
            case 'disadvantage': return 'text-red-400 bg-red-800/10 text-sm';
            case 'neutral': return 'text-yellow-400 bg-yellow-800/10 text-sm';
            default: return 'text-zinc-300 bg-zinc-700/40 text-sm';
        }
    };

    const getConfidenceColor = (score) => {
        if (score === null || score === undefined) return 'text-zinc-400';
        if (score >= 7) return 'text-green-400';
        if (score >= 4) return 'text-yellow-400';
        return 'text-red-400';
    };

    const hasActionableTalkingPoints = actionableTalkingPoints && actionableTalkingPoints.length > 0;
    const hasSources = sources && sources.length > 0 && sources.some(s => s.description || s.url);

    // Function to toggle reasoning visibility
    const toggleReasoning = (id) => {
        setExpandedReasoning(prev => ({ ...prev, [id]: !prev[id] }));
    };

    return (
        <div className="h-full flex flex-col bg-zinc-950/25 text-zinc-100 p-4 md:p-6">
            {/* Header */}
            <div className="mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl text-zinc-100">
                    Competitive Analysis Overview <span className="text-amber-400 text-sm align-middle">* tech preview</span>
                </h2>
            </div>

            <Tabs value={activeTab} className="flex-grow flex flex-col">
                <TabsHeader
                    className="rounded-none  bg-transparent p-0 mb-5"
                    indicatorProps={{
                        className: "bg-transparent border-b-2 border-sky-500 shadow-none rounded-none",
                    }}
                >
                    <Tab
                        value="Talking Points"
                        onClick={() => setActiveTab("Talking Points")}
                        className={`py-2.5 px-3.5 text-base   transition-colors duration-150 ease-in-out mr-2 rounded-t-md
                            ${activeTab === 'Talking Points' ? 'text-zinc-100' : 'text-zinc-400 hover:text-zinc-100'}`
                        }
                    >
                        Talking Points & Sources
                    </Tab>
                    <Tab
                        value="Competitive Comparisons"
                        onClick={() => setActiveTab("Competitive Comparisons")}
                        className={`py-2.5 px-3.5 text-base   transition-colors duration-150 ease-in-out rounded-t-md
                            ${activeTab === 'Competitive Comparisons' ? 'text-zinc-100' : 'text-zinc-400 hover:text-zinc-100'}`
                        }
                    >
                        Competitive Comparisons
                    </Tab>
                </TabsHeader>
                <TabsBody className="flex-grow overflow-y-auto custom-scrollbar pr-1">
                    <TabPanel value="Talking Points" className="p-0 flex-grow">
                        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                            {/* Column 1: Actionable Talking Points */}
                            <div className="  p-5 rounded-xl shadow-lg flex flex-col">
                                 
                                <h3 className="text-xl   text-zinc-100 mb-5   flex items-center">
                                Actionable Talking Points
                                </h3>
                                {hasActionableTalkingPoints ? (
                                    <ul className="space-y-4 overflow-y-auto custom-scrollbar flex-grow pr-2">
                                        {actionableTalkingPoints.map((point, index) => (
                                            <li key={index} className="p-4  -ml-4 rounded-lg  transition-all ">
                                          <div className="flex items-start">
                                                    <span className="mr-3 mt-0.5 flex-shrink-0 h-8 w-8 rounded-full bg-sky-500/75 text-white flex items-center justify-center text-base font-bold">{index + 1}</span>
                                                    <p className="text-base text-zinc-200 leading-relaxed ml-4">{point}</p>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : <div className="flex-grow flex items-center justify-center"><p className="italic text-zinc-500 text-sm">No actionable talking points available.</p></div>}
                            </div>

                            {/* Column 2: Sources */}
                            <div className=" p-5 rounded-xl  flex flex-col">
                                <h3 className="text-xl   text-zinc-100 mb-5 ml-4 flex items-center">
                                    Sources
                                </h3>
                                {hasSources ? (
                                    <ul className="space-y-3.5 overflow-y-auto custom-scrollbar flex-grow pr-2">
                                        {sources.filter(s => s.description || s.url).map((source, index) => {
                                            let domain = '';
                                            if (source.url) {
                                                try {
                                                    domain = new URL(source.url).hostname;
                                                } catch (e) {
                                                    console.error("Invalid URL for favicon:", source.url);
                                                }
                                            }
                                            const faviconUrl = domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=16` : '';

                                            const handleItemClick = () => {
                                                if (source.url) {
                                                    window.open(source.url, '_blank', 'noopener,noreferrer');
                                                }
                                            };

                                            return (
                                                <motion.div
                                                    key={index}
                                                    className={`p-5 rounded-lg bg-zinc-900 transition-all ${source.url ? 'cursor-pointer hover:bg-zinc-800/70' : ''}`}
                                                    onClick={handleItemClick}
                                                    whileHover={source.url ? { scale: 1.01 } : {}}
                                                    whileTap={source.url ? { scale: 0.98 } : {}}
                                                >
                                                    <div className="flex items-center">
                                                        {faviconUrl && source.url && (
                                                            <img
                                                                src={faviconUrl}
                                                                alt="favicon"
                                                                className="w-6 h-6 mr-4 flex-shrink-0"
                                                                onError={(e) => { e.target.style.display = 'none'; }} // Hide if favicon fails to load
                                                            />
                                                        )}
                                                        <div className="flex-grow overflow-hidden">
                                                            {source.url ? (
                                                                <span className={`text-base ${source.url ? 'text-zinc-100 group-hover:text-sky-300' : 'text-zinc-200'} break-all transition-colors truncate`}>
                                                                    {source.description || source.url}
                                                                </span>
                                                            ) : (
                                                                 <p className="text-base text-zinc-300 leading-normal">{source.description}</p>
                                                            )}
                                                        </div>
                                                        <ArrowTopRightOnSquareIcon className="w-4 h-4 text-zinc-400" />
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </ul>
                                ) : <div className="flex-grow flex items-center justify-center"><p className="italic text-zinc-500 text-sm">No sources data available.</p></div>}
                            </div>
                        </div>
                    </TabPanel>
                    <TabPanel value="Competitive Comparisons" className="p-0 flex-grow flex flex-col">
                        {comparisonTable && comparisonTable.length > 0 ? (
                            <div className="flex-grow overflow-hidden flex flex-col">
                                {/* Static Header Row for Div Table */}
                                <div className="flex items-stretch gap-x-2 md:gap-x-3 mb-2 px-2 py-3 bg-zinc-800/50 text-xs font-medium uppercase tracking-wider text-zinc-200 sticky top-0 z-10 shadow">
                                    <div className="basis-1/4 flex-shrink-0 pl-2">Feature / Metric & Our Product</div>
                                    {competitorNames.map((name) => (
                                        <div key={name} className="flex-1 basis-0 min-w-[100px] md:min-w-[120px] pl-1 truncate" title={name}>
                                            {name}
                                        </div>
                                    ))}
                                </div>

                                {/* Scrollable Content Area for Rows */}
                                <div className="overflow-y-auto flex-grow custom-scrollbar pr-1 pb-4 rounded-b-lg  "> {/* Added rounded-b-lg and bg */}
                                    {comparisonTable.map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex items-stretch gap-x-2 md:gap-x-3 p-3 my-1.5  rounded-lg shadow transition-all duration-150 ease-in-out first:mt-0"
                                        >
                                            {/* Column 1: Feature/Metric, Our Product & KPIs */}
                                            <div className="basis-1/4 flex-shrink-0 pt-1 pr-8">
                                                <p className="text-sm font-medium text-zinc-100 md:text-base leading-snug mb-2.5 pr-8">{item.featureOrMetric}</p>
                                                <p className="text-xs text-zinc-200 md:text-sm leading-snug whitespace-normal break-words mb-2.5 pr-8 pt-1">{item.ourProduct}</p>
                                                
                                                <div className="flex gap-x-2 md:gap-x-3 pr-8">
                                                    {/* Score KPI Card */}
                                                    {item.categoryScore !== undefined && item.categoryScore !== null && (
                                                        <div className="flex-1 p-2.5 bg-zinc-800/25 rounded-lg shadow-md space-y-1.5 min-w-0">
                                                            <div className="flex justify-between items-center mb-0.5">
                                                                <div className="flex items-center space-x-1.5">
                                                                    <span className="  text-zinc-200  text-sm tracking-wider">Score</span>
                                                                    {item.categoryScoreReasons && item.categoryScoreReasons.length > 0 && (
                                                                        <button 
                                                                            onClick={() => toggleReasoning(`score-${index}`)}
                                                                            className="text-sky-400 hover:text-sky-300 text-[9px] md:text-[10px] font-medium py-0 px-1 rounded bg-sky-500/10 hover:bg-sky-500/20 transition-colors focus:outline-none"
                                                                        >
                                                                            {expandedReasoning[`score-${index}`] ? 'Why -' : 'Why +'}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                <span className="text-xl md:text-3xl  text-sky-300">
                                                                    {item.categoryScore}
                                                                </span>
                                                            </div>
                                                            {expandedReasoning[`score-${index}`] && item.categoryScoreReasons && item.categoryScoreReasons.length > 0 && (
                                                                <div className="text-[10px] md:text-xs text-zinc-300 leading-normal pt-0.5 animate-fadeIn whitespace-normal break-words">
                                                                    <ul className="list-inside list-disc space-y-0.5">
                                                                        {item.categoryScoreReasons.map((reason, idx_reason) => (
                                                                            <li key={idx_reason}>{reason}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Confidence KPI Card */}
                                                    {item.confidenceScore !== undefined && item.confidenceScore !== null && (
                                                        <div className="flex-1 p-2.5 bg-zinc-800/25 rounded-lg shadow-md space-y-1.5 min-w-0">
                                                            <div className="flex justify-between items-center mb-0.5">
                                                                <div className="flex items-center space-x-1.5">
                                                                    <span className="text-sm  text-zinc-200   tracking-wider">Sources</span>
                                                                    {item.confidenceReasoning && item.confidenceReasoning.length > 0 && (
                                                                        <button 
                                                                            onClick={() => toggleReasoning(`confidence-${index}`)}
                                                                            className="text-sky-400 hover:text-sky-300 text-[9px] md:text-[10px] font-medium py-0 px-1 rounded bg-sky-500/10 hover:bg-sky-500/20 transition-colors focus:outline-none"
                                                                        >
                                                                            {expandedReasoning[`confidence-${index}`] ? 'Why -' : 'Why +'}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                <span className={`text-xl md:text-3xl ${getConfidenceColor(item.confidenceScore)}`}>
                                                                    {item.confidenceScore}
                                                                </span>
                                                            </div>
                                                            {expandedReasoning[`confidence-${index}`] && item.confidenceReasoning && item.confidenceReasoning.length > 0 && (
                                                                <p className="text-[10px] md:text-xs text-zinc-300 leading-normal pt-0.5 animate-fadeIn whitespace-normal break-words">
                                                                    {item.confidenceReasoning.join('; ')}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Competitor Columns */}
                                            {competitorNames.map(competitorName => {
                                                const competitorData = item.competitors.find(c => c.competitorName === competitorName);
                                                return (
                                                    <div key={competitorName} className="flex flex-col flex-1 basis-0 min-w-[100px] md:min-w-[120px] space-y-0">
                                                        {competitorData ? (
                                                            <>
                                                                {competitorData.competitorFUD && (
                                                                    <div className="flex-1 bg-zinc-900/70 p-4 rounded-t-lg shadow-sm overflow-hidden">
                                                                        <p className="text-[11px] font-semibold text-zinc-200 md:text-sm mb-1">
                                                                            Their Possible Angle
                                                                        </p>
                                                                        <p className="text-[11px] text-zinc-300 md:text-sm pb-2 leading-snug whitespace-normal break-words">{competitorData.competitorFUD}</p>
                                                                    </div>
                                                                )}
                                                                <div className={`flex-1 p-4 shadow-sm ${getAdvantageClass(competitorData.competitorAdvantage)} ${competitorData.competitorFUD ? 'rounded-b-lg' : 'rounded-lg'} leading-snug whitespace-normal break-words overflow-hidden`}>
                                                                 <p className="text-[11px]  font-semibold text-zinc-200 md:text-sm mb-1">
                                                                         Likely Reality
                                                                     </p>
                                                                     <p className="text-[11px]   md:text-sm pb-2 leading-snug whitespace-normal break-words">{competitorData.competitorPosition}</p>
                                                                
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <span className="block rounded-md bg-zinc-700/50 p-2 text-xs text-zinc-500 italic h-full">N/A</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                             <div className="flex-grow flex flex-col items-center justify-center text-zinc-400 p-8 text-center">
                                 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 mb-3 text-zinc-500">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21M9 17.25v-1.5s0-.08.08-.08h4.42a.996.996 0 0 0 .916-.584l1.153-3.458a1.125 1.125 0 0 0-2.128-.711L12.125 15H9v2.25ZM9 17.25H7.875a1.125 1.125 0 0 1-1.125-1.125V9.375c0-.621.504-1.125 1.125-1.125H16.5c.621 0 1.125.504 1.125 1.125v2.25" />
                                 </svg>
                                 <h3 className="text-lg font-semibold text-zinc-200 mb-1">Comparison Data Not Available</h3>
                                 <p className="text-zinc-400 text-sm">No specific comparison details were found in this analysis.</p>
                             </div>
                        )}
                    </TabPanel>
                </TabsBody>
            </Tabs>

        </div>
    );
};

export default PathCompetitiveAnalysis;